import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

export interface PermissionAdminItem {
  uuid: string
  code: string
  description: string | null
  roleGroupsCount: number
}

interface RoleGroupPermissionRef {
  uuid: string
  code: string
  description: string | null
}

export interface RoleGroupAdminListItem {
  uuid: string
  name: string
  description: string | null
  adGroupDn: string | null
  permissions: RoleGroupPermissionRef[]
  membersCount: number
  dateCreated: string
  dateUpdated: string
}

interface RoleGroupMember {
  membershipUuid: string
  userUuid: string
  email: string
  firstName: string | null
  lastName: string | null
  status: string
  joinedAt: string
}

export interface RoleGroupAdminDetail extends RoleGroupAdminListItem {
  members: RoleGroupMember[]
}

export interface UserSearchResult {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
}

export interface CreatePermissionInput {
  code: string
  description?: string | null
}

export interface CreateRoleGroupInput {
  name: string
  description?: string | null
  adGroupDn?: string | null
  permissionUuids?: string[]
}

export interface UpdateRoleGroupInput {
  name?: string
  description?: string | null
  adGroupDn?: string | null
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

export const useAccessControlAdminStore = defineStore('accessControlAdmin', () => {
  const suiteStore = useSuiteStore()

  const permissions = ref<PermissionAdminItem[]>([])
  const roleGroups = ref<RoleGroupAdminListItem[]>([])
  const isLoadingPermissions = ref(false)
  const isLoadingRoleGroups = ref(false)

  // ----- Permissions -----
  async function fetchPermissions(): Promise<PermissionAdminItem[]> {
    isLoadingPermissions.value = true
    try {
      const res = await suiteStore.suiteFetch('permissions/admin', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        permissions.value = (await res.json()) as PermissionAdminItem[]
        return permissions.value
      }
    } catch (e) {
      console.error('Erreur récupération permissions admin', e)
    } finally {
      isLoadingPermissions.value = false
    }
    return []
  }

  async function createPermission(input: CreatePermissionInput): Promise<StoreResult<PermissionAdminItem>> {
    const res = await suiteStore.suiteFetch('permissions/admin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PermissionAdminItem
      permissions.value.push(data)
      permissions.value.sort((a, b) => a.code.localeCompare(b.code))
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readErrorMessage(res, 'Erreur lors de la création de la permission')
      : 'Erreur lors de la création de la permission'
    return { ok: false, message }
  }

  async function updatePermission(uuid: string, description: string | null): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`permissions/admin/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const updated = (await res.json()) as PermissionAdminItem
      const idx = permissions.value.findIndex((p) => p.uuid === uuid)
      if (idx >= 0) permissions.value[idx] = updated
      return true
    }
    return false
  }

  async function deletePermission(uuid: string): Promise<StoreResult<true>> {
    const res = await suiteStore.suiteFetch(`permissions/admin/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      permissions.value = permissions.value.filter((p) => p.uuid !== uuid)
      return { ok: true, data: true }
    }
    const message = res && typeof res !== 'boolean'
      ? await readErrorMessage(res, 'Suppression impossible')
      : 'Suppression impossible'
    return { ok: false, message }
  }

  // ----- Role groups -----
  async function fetchRoleGroups(): Promise<RoleGroupAdminListItem[]> {
    isLoadingRoleGroups.value = true
    try {
      const res = await suiteStore.suiteFetch('role-groups/admin', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        roleGroups.value = (await res.json()) as RoleGroupAdminListItem[]
        return roleGroups.value
      }
    } catch (e) {
      console.error('Erreur récupération role groups admin', e)
    } finally {
      isLoadingRoleGroups.value = false
    }
    return []
  }

  async function fetchRoleGroupDetail(uuid: string): Promise<RoleGroupAdminDetail | null> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as RoleGroupAdminDetail
    }
    return null
  }

  async function createRoleGroup(input: CreateRoleGroupInput): Promise<StoreResult<RoleGroupAdminListItem>> {
    const res = await suiteStore.suiteFetch('role-groups/admin', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as RoleGroupAdminListItem
      roleGroups.value.push(data)
      roleGroups.value.sort((a, b) => a.name.localeCompare(b.name))
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readErrorMessage(res, 'Erreur lors de la création du groupe')
      : 'Erreur lors de la création du groupe'
    return { ok: false, message }
  }

  async function updateRoleGroup(uuid: string, input: UpdateRoleGroupInput): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const updated = (await res.json()) as RoleGroupAdminListItem
      const idx = roleGroups.value.findIndex((g) => g.uuid === uuid)
      if (idx >= 0) roleGroups.value[idx] = updated
      return true
    }
    return false
  }

  async function deleteRoleGroup(uuid: string): Promise<StoreResult<true>> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      roleGroups.value = roleGroups.value.filter((g) => g.uuid !== uuid)
      return { ok: true, data: true }
    }
    const message = res && typeof res !== 'boolean'
      ? await readErrorMessage(res, 'Suppression impossible')
      : 'Suppression impossible'
    return { ok: false, message }
  }

  async function addPermissionToGroup(roleGroupUuid: string, permissionUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${roleGroupUuid}/permissions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissionUuid }),
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  async function removePermissionFromGroup(roleGroupUuid: string, permissionUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${roleGroupUuid}/permissions/${permissionUuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  async function searchUsers(search: string): Promise<UserSearchResult[]> {
    const q = new URLSearchParams({ search, limit: '15', status: 'ACTIVE' })
    const res = await suiteStore.suiteFetch(`users/admin/list?${q}`, { method: 'GET', credentials: 'include' })
    if (res && typeof res !== 'boolean' && res.ok) {
      const json = await res.json()
      return (json.data ?? []) as UserSearchResult[]
    }
    return []
  }

  async function addMemberToGroup(roleGroupUuid: string, userUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${roleGroupUuid}/members`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userUuid }),
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  async function removeMemberFromGroup(roleGroupUuid: string, membershipUuid: string): Promise<boolean> {
    const res = await suiteStore.suiteFetch(`role-groups/admin/${roleGroupUuid}/members/${membershipUuid}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    return !!(res && typeof res !== 'boolean' && res.ok)
  }

  return {
    permissions,
    roleGroups,
    isLoadingPermissions,
    isLoadingRoleGroups,
    fetchPermissions,
    createPermission,
    updatePermission,
    deletePermission,
    fetchRoleGroups,
    fetchRoleGroupDetail,
    createRoleGroup,
    updateRoleGroup,
    deleteRoleGroup,
    addPermissionToGroup,
    removePermissionFromGroup,
    searchUsers,
    addMemberToGroup,
    removeMemberFromGroup,
  }
})
